import {
  TextField,
  Grid,
  Button,
  FormHelperText,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Link
} from '@mui/material';

import {Formik, Form} from 'formik';

import {useCallback} from 'react';

import {useTranslation} from 'react-i18next';

import {useDispatch} from 'react-redux';

import {
  resetAddressData,
  resetBillingData,
  setBillingData
} from '@/redux-store/slices/billing-data';
import {useAppSelector} from '@/redux-store/store';

import {AddressDetails} from '@/types/billing-data';
import TypographyTitle from '@/components/ui/typography-title';
import LocationAutocomplete from '@/components/forms/input/location/location-autocomplete';
import BillingDataValidationSchema, {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH
} from '@/schemas/billing-data';

interface BillingDataFormProps {
  onCancel?: () => void;
  onSubmit?: () => void;
  onError?: (error: Error) => void;
  withSubscription?: boolean;
  termsOfUseLink?: string;
  cancellationPolicyLink?: string;
}

const BillingDataForm: React.FC<BillingDataFormProps> = ({
  onCancel,
  onSubmit,
  onError,
  withSubscription = false,
  termsOfUseLink,
  cancellationPolicyLink
}) => {
  const {t} = useTranslation();

  const billingDataValidationSchema = BillingDataValidationSchema(
    t,
    withSubscription,
    !!cancellationPolicyLink
  );

  const initialValues = useAppSelector(
    (state) => state.billingData.billingData
  );
  const userInfos = useAppSelector((state) => state.userInfos.userInfos);

  const dispatch = useDispatch();

  const handleSubmitForm = useCallback(() => {
    try {
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
    }
  }, [onError, onSubmit]);

  const resetFormData = useCallback(() => {
    dispatch(resetBillingData());
  }, [dispatch]);

  const computedInitialState = {
    ...initialValues,
    name: initialValues.name || userInfos.name,
    email: initialValues.email || userInfos.email,
    subscription: null
  };

  return (
    <>
      <Formik
        initialValues={computedInitialState}
        validationSchema={billingDataValidationSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values) => {
          dispatch(setBillingData(values));
          handleSubmitForm();
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          validateForm,
          setTouched
        }) => {
          // console.log('errors', errors);

          // console.log('values: ', values);

          return (
            <Form>
              <TypographyTitle textKey={t('billing_data')} />
              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t('prop_user_name')}
                    value={values.name}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setBillingData({
                          name: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                  />
                  <FormHelperText>
                    {values.name.length}/ {NAME_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1}>
                <Grid item xs={12}>
                  <LocationAutocomplete
                    initialAddress={initialValues.address?.fullAddress || ''}
                    onAddressSelected={(address: AddressDetails) => {
                      setFieldValue('address', address);
                      dispatch(
                        setBillingData({
                          address: address
                        })
                      );
                    }}
                    error={
                      touched.address && errors.address
                        ? errors.address
                        : undefined
                    }
                    touched={!!touched['address']}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} mt={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    name="email"
                    label={t('email')}
                    value={values.email}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setBillingData({
                          email: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    fullWidth
                  />
                  <FormHelperText>
                    {values.name.length}/ {EMAIL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              {withSubscription && (
                <>
                  <Grid container spacing={2} my={1} justifyContent="center">
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <FormControl component="fieldset">
                        <RadioGroup
                          aria-label="subscription"
                          name="subscription"
                          value={values.subscription}
                          onChange={(e) => {
                            setFieldValue('subscription', e.target.value);
                            dispatch(
                              setBillingData({
                                subscription: e.target.value as
                                  | 'mh'
                                  | 'wh'
                                  | 'no'
                              })
                            );
                          }}
                        >
                          <FormControlLabel
                            value="mh"
                            control={<Radio />}
                            label={t('subscription_mh')}
                          />
                          <FormControlLabel
                            value="wh"
                            control={<Radio />}
                            label={t('subscription_wh')}
                          />
                          <FormControlLabel
                            value={'no'}
                            control={<Radio />}
                            label={t('subscription_no')}
                          />
                        </RadioGroup>
                        {touched.subscription &&
                          Boolean(errors.subscription) && (
                            <FormHelperText error>
                              {errors.subscription}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}

              <Grid container spacing={2} my={1}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center'
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.termsOfUseAccepted}
                          onChange={(e) => {
                            setFieldValue(
                              'termsOfUseAccepted',
                              e.target.checked
                            );
                          }}
                          name="termsOfUseAccepted"
                        />
                      }
                      label={
                        <span>
                          {t('accept_terms')}{' '}
                          <Link
                            href={termsOfUseLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t('the_terms_of_participation')}
                          </Link>
                          <span style={{whiteSpace: 'nowrap'}}>
                            {' '}
                            {t('of_provider')}
                          </span>
                        </span>
                      }
                    />
                  </FormGroup>
                  {touched.termsOfUseAccepted && errors.termsOfUseAccepted && (
                    <FormHelperText error>
                      {errors.termsOfUseAccepted}
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>

              {cancellationPolicyLink && (
                <Grid container spacing={2} my={1}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.cancellationPolicyAccepted}
                            onChange={(e) => {
                              setFieldValue(
                                'cancellationPolicyAccepted',
                                e.target.checked
                              );
                            }}
                            name="cancellationPolicyAccepted"
                          />
                        }
                        label={
                          <span>
                            {t('accept')}{' '}
                            <Link
                              href={cancellationPolicyLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('the_cancellation_policy')}
                            </Link>
                            <span style={{whiteSpace: 'nowrap'}}>
                              {' '}
                              {t('of_provider')}
                            </span>
                          </span>
                        }
                      />
                    </FormGroup>
                    {touched.cancellationPolicyAccepted &&
                      errors.cancellationPolicyAccepted && (
                        <FormHelperText error>
                          {errors.cancellationPolicyAccepted}
                        </FormHelperText>
                      )}
                  </Grid>
                </Grid>
              )}

              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                spacing={2}
                sx={{
                  my: {xs: 1, sm: 3}
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      resetFormData();
                      if (withSubscription) {
                        if (onCancel) {
                          onCancel();
                        }
                      } else {
                        const errors = await validateForm();
                        if (
                          !errors.termsOfUseAccepted &&
                          !errors.cancellationPolicyAccepted
                        ) {
                          dispatch(resetAddressData());
                          dispatch(
                            setBillingData({
                              name: values.name,
                              email: values.email,
                              termsOfUseAccepted: true,
                              cancellationPolicyAccepted: true
                            })
                          );
                          handleSubmitForm();
                        } else {
                          setTouched({
                            termsOfUseAccepted: true,
                            cancellationPolicyAccepted: true
                          });
                        }
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {withSubscription
                      ? t('cancel')
                      : t('no_address_in_invoice_needed')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    marginTop: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      handleSubmit();
                    }}
                    sx={{
                      width: {xs: '50%', sm: '80%'}
                    }}
                  >
                    {t('confirm_and_continue')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default BillingDataForm;
